import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Bell, Check, Loader2 } from 'lucide-react';

const SANS = { fontFamily: '"DM Sans", "Space Grotesk", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' };

const T = {
  da: {
    done: (email) => `Tak! Vi giver besked på ${email}`,
    open: 'Få besked når den er på lager',
    placeholder: 'Din email',
    error: 'Indtast en gyldig email.',
    saving: 'Gemmer...',
    submit: 'Giv mig besked',
  },
  en: {
    done: (email) => `Thanks! We'll notify you at ${email}`,
    open: 'Notify me when back in stock',
    placeholder: 'Your email',
    error: 'Enter a valid email.',
    saving: 'Saving...',
    submit: 'Notify me',
  },
};

/**
 * Reusable "Notify me when in stock" email capture.
 * Saves a StockNotification record for the given charger.
 * variant: 'light' (default) | 'dark' · lang: 'da' | 'en'
 */
export default function StockNotifyForm({ chargerSku, chargerName, sourcePage = 'opladere', variant = 'light', lang = 'da' }) {
  const t = T[lang] || T.da;
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | saving | done | error

  const isDark = variant === 'dark';

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus('error');
      return;
    }
    setStatus('saving');
    try {
      await base44.entities.StockNotification.create({
        email: email.trim().toLowerCase(),
        charger_sku: chargerSku,
        charger_name: chargerName,
        source_page: sourcePage,
        notified: false,
      });
      setStatus('done');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'done') {
    return (
      <div className={`w-full flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold ${isDark ? 'bg-[#314A56] text-white border border-[#D4A62A]/40' : 'bg-[#f0faf0] text-[#2F424D] border border-[#d0e8d0]'}`} style={SANS}>
        <Check className="w-4 h-4" /> {t.done(email)}
      </div>
    );
  }

  if (!open) {
    return (
      <Button
        onClick={() => setOpen(true)}
        className={`w-full font-bold rounded-xl h-11 transition-all ${isDark ? 'bg-[#F1EBDD] hover:bg-[#E9DFC9] text-[#21343D] border border-[#D4A62A]/55' : 'bg-[#2F424D] hover:bg-[#263A43] text-white'}`}
        style={SANS}
      >
        <Bell className="w-4 h-4 mr-2" /> {t.open}
      </Button>
    );
  }

  return (
    <form onSubmit={submit} className="w-full space-y-2">
      <Input
        type="email"
        autoFocus
        value={email}
        onChange={(e) => { setEmail(e.target.value); if (status === 'error') setStatus('idle'); }}
        placeholder={t.placeholder}
        className={isDark ? 'bg-white/95 border-white/20 h-11' : 'h-11'}
        style={SANS}
      />
      {status === 'error' && <p className="text-xs text-red-500" style={SANS}>{t.error}</p>}
      <Button
        type="submit"
        disabled={status === 'saving'}
        className={`w-full font-bold rounded-xl h-11 transition-all ${isDark ? 'bg-[#F1EBDD] hover:bg-[#E9DFC9] text-[#21343D] border border-[#D4A62A]/55' : 'bg-[#D4A62A] hover:bg-[#c29625] text-[#2F424D]'}`}
        style={SANS}
      >
        {status === 'saving' ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t.saving}</> : <>{t.submit}</>}
      </Button>
    </form>
  );
}